<template>
  <div class="usage-page">
    <div class="usage-heading">
      <div>
        <h2>
          <el-icon><DataAnalysis /></el-icon>使用统计
        </h2>
        <p>按用户查看后台访问情况，了解使用频率与活跃程度。</p>
      </div>
      <el-tag v-if="report?.demo" type="warning" effect="plain"
        >本地隔离测试数据</el-tag
      >
    </div>

    <template v-if="allowed">
      <div class="usage-filters">
        <el-input
          v-model="keyword"
          placeholder="搜索用户名、姓名或用户ID"
          clearable
          @keyup.enter="search"
          style="width: 260px"
        />
        <el-date-picker
          v-model="dates"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          range-separator="至"
          :clearable="false"
          style="width: 280px; flex-grow: 0"
        />
        <el-button
          type="primary"
          :icon="Search"
          :loading="loading"
          @click="search"
          >查询</el-button
        >
        <el-button @click="reset">最近7天</el-button>
        <el-button :icon="Refresh" :loading="loading" @click="load"
          >刷新</el-button
        >
      </div>

      <el-alert
        v-if="error"
        :title="error"
        type="error"
        :closable="false"
        show-icon
        class="usage-error"
      />

      <div class="usage-cards" v-loading="loading">
        <div v-for="card in cards" :key="card.label" class="usage-card">
          <span>{{ card.label }}</span
          ><strong>{{ card.value }}</strong
          ><small>{{ card.note }}</small>
        </div>
      </div>

      <div class="usage-table-card">
        <div class="usage-table-heading">
          <h3>用户使用明细</h3>
          <span
            >共 {{ report?.total || 0 }} 位用户 · 合计按全部筛选结果计算</span
          >
        </div>
        <el-table
          :data="report?.records || []"
          v-loading="loading"
          stripe
          empty-text="当前筛选条件下暂无用户数据"
          style="width: 100%"
        >
          <el-table-column label="用户" min-width="170" fixed>
            <template #default="{ row }"
              ><div class="usage-user">
                <strong>{{ row.userName || "未命名用户" }}</strong
                ><small>{{ row.realName || "—" }}</small
                ><small>ID {{ row.userId }}</small>
              </div></template
            >
          </el-table-column>
          <el-table-column
            prop="loginCount"
            label="登录次数"
            width="100"
            align="right"
          />
          <el-table-column
            prop="requestCount"
            label="接口访问次数"
            width="120"
            align="right"
          />
          <el-table-column
            prop="sessionCount"
            label="使用次数"
            width="145"
            align="right"
          />
          <el-table-column
            prop="activeDays"
            label="活跃天数"
            width="100"
            align="right"
          />
          <el-table-column label="使用时长" width="155" align="right"
            ><template #default="{ row }">{{
              formatUsageDuration(row.activeMillis)
            }}</template></el-table-column
          >
          <el-table-column label="接口上传量" width="160" align="right"
            ><template #default="{ row }">{{
              formatUsageBytes(row.receivedBytes)
            }}</template></el-table-column
          >
          <el-table-column label="接口下载量" width="120" align="right"
            ><template #default="{ row }">{{
              formatUsageBytes(row.sentBytes)
            }}</template></el-table-column
          >
          <el-table-column label="数据下载量" width="130" align="right"
            ><template #default="{ row }">{{
              formatUsageBytes(row.dataDownloadedBytes)
            }}</template></el-table-column
          >
          <el-table-column label="最近登录" width="175"
            ><template #default="{ row }">{{
              formatTime(row.lastLogin)
            }}</template></el-table-column
          >
          <el-table-column label="最近访问" width="175"
            ><template #default="{ row }">{{
              formatTime(row.lastAccess)
            }}</template></el-table-column
          >
        </el-table>
        <el-pagination
          v-model:current-page="pageNum"
          v-model:page-size="pageSize"
          :page-sizes="[10, 20, 50]"
          :total="report?.total || 0"
          layout="total, sizes, prev, pager, next"
          @current-change="load"
          @size-change="search"
          class="usage-pagination"
        />
      </div>

      <div class="usage-notes">
        <h3>统计口径</h3>
        <p>
          按北京时间统计启用后的已登录请求。登录成功计入访问，刷新令牌、退出、设备匿名上传及统计页查询不计入；最近登录和最近访问显示所选日期内的最后记录。
        </p>
        <p>
          超过30分钟无请求，再访问记为新一次使用；两次请求间隔不超过30分钟时，累计其时间差作为使用时长，跨日按实际日期拆分。该数值不代表精确在线时长，单次访问可能显示0分钟，自动请求也可能增加次数和时长。
        </p>
        <p>
          接口上传量按已知请求体长度统计，长度未知时记0；接口下载量统计接口响应体，不含下方的数据下载量。
        </p>
        <p>
          数据下载量从功能启用后开始累计，统计当前网页中检测原图、识别图的实际响应体字节，包含列表、详情和预览加载。同一图片在页面内复用缓存不重复计量，重新传输则重新累计；历史图片下载无法补算。暂不含视频、头像、其他客户端直接访问OSS、请求头和设备流量卡用量。
        </p>
      </div>
    </template>
    <el-empty v-else description="仅管理员可查看使用统计" />
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import { DataAnalysis, Refresh, Search } from "@element-plus/icons-vue";
import { getUserUsage } from "@/api/usage";
import {
  formatUsageDuration,
  formatUsageBytes,
  usageDateRange,
} from "@/utils/usage";

const store = useStore();
const allowed = computed(() => store.getters.isAdminOrAbove);
const dates = ref(usageDateRange());
const keyword = ref("");
const pageNum = ref(1);
const pageSize = ref(10);
const loading = ref(false);
const error = ref("");
const report = ref(null);
let generation = 0;
const cards = computed(() => {
  const s = report.value?.summary || {};
  return [
    {
      label: "活跃用户",
      value: `${s.activeUsers || 0} / ${s.userCount || 0}`,
      note: "活跃人数 / 筛选用户数",
    },
    {
      label: "登录次数",
      value: s.loginCount || 0,
      note: "登录成功，排除令牌刷新",
    },
    {
      label: "接口访问次数",
      value: s.requestCount || 0,
      note: "已登录用户的后台请求",
    },
    {
      label: "使用次数",
      value: s.sessionCount || 0,
      note: "超过30分钟间隔分为新一次",
    },
    {
      label: "使用时长",
      value: formatUsageDuration(s.activeMillis),
      note: "连续请求间隔累计",
    },
    {
      label: "数据下载量",
      value: formatUsageBytes(s.dataDownloadedBytes),
      note: "检测原图及识别图的实际传输量",
    },
  ];
});
const formatTime = (value) =>
  value ? value.replace("T", " ").slice(0, 19) : "—";

async function load() {
  if (!allowed.value) return;
  if (!dates.value || dates.value.length !== 2) {
    generation++;
    report.value = null;
    loading.value = false;
    error.value = "请选择完整的日期范围";
    return;
  }
  const current = ++generation;
  loading.value = true;
  error.value = "";
  try {
    const response = await getUserUsage({
      startDate: dates.value[0],
      endDate: dates.value[1],
      keyword: keyword.value.trim(),
      pageNum: pageNum.value,
      pageSize: pageSize.value,
    });
    if (current !== generation) return;
    if (response.code !== 0)
      throw new Error(response.message || "获取使用统计失败");
    report.value = response.data;
  } catch (failure) {
    if (current === generation) {
      report.value = null;
      error.value = failure.message || "获取使用统计失败，请重试";
    }
  } finally {
    if (current === generation) loading.value = false;
  }
}
function search() {
  pageNum.value = 1;
  load();
}
function reset() {
  dates.value = usageDateRange();
  keyword.value = "";
  search();
}
watch(allowed, (value) => {
  if (value) load();
});
onMounted(() => {
  if (allowed.value) load();
});
</script>

<style scoped>
.usage-page {
  color: #253c45;
}
.usage-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 22px;
}
.usage-heading h2 {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #168b76;
  margin: 0 0 8px;
  font-size: 22px;
}
.usage-heading p {
  color: #7d8994;
  margin: 0;
  font-size: 14px;
}
.usage-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  background: white;
  padding: 20px;
  border: 1px solid #e5eeee;
  border-radius: 10px;
}
.usage-error {
  margin-top: 16px;
}
.usage-cards {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin: 20px 0;
}
.usage-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 22px;
  background: white;
  border: 1px solid #e5eeee;
  border-radius: 10px;
}
.usage-card span {
  color: #677b83;
  font-size: 14px;
}
.usage-card strong {
  color: #168b76;
  font-size: 26px;
  font-weight: 650;
}
.usage-card small {
  color: #93a0a5;
}
.usage-table-card {
  padding: 20px;
  border: 1px solid #e5eeee;
  background: white;
  border-radius: 10px;
}
.usage-table-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 14px;
}
.usage-table-heading h3 {
  margin: 0;
  font-size: 16px;
}
.usage-table-heading span {
  color: #8b999f;
  font-size: 12px;
}
.usage-user {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 7px 0;
}
.usage-user small {
  font-size: 12px;
  color: #8b999f;
}
.usage-pagination {
  margin-top: 20px;
  justify-content: flex-end;
}
.usage-notes {
  margin-top: 20px;
  padding: 16px 20px;
  background: #f1f8f6;
  border-radius: 10px;
  color: #687e7a;
  font-size: 12px;
  line-height: 1.8;
}
.usage-notes h3 {
  margin: 0 0 8px;
  font-size: 13px;
  color: #367c6c;
}
.usage-notes p {
  margin: 6px 0;
}
@media (max-width: 1000px) {
  .usage-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 600px) {
  .usage-cards {
    grid-template-columns: 1fr;
  }
  .usage-heading,
  .usage-table-heading {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
